import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { UserState, AppUser, UserFilters } from "../../types/user";
import { UserService } from "../../services/userService";
import { ApiService } from "../../services/apiService";

const initialState: UserState = {
  users: [],
  llmModelsPlans: [],
  userDetails: {} as AppUser,
  isLoading: false,
  isPaginationLoading: false, // Separate loading state for pagination
  error: null,
  overallStats: {
    total_users: 0,
    total_active_users: 0,
    active_users: 0,
    subscribed_users: 0,
    total_revenue: 0,
    test_users: 0,
    real_users: 0,
  },
  filters: {
    search: "",
    status: "",
    subscriptionPlan: "",
    userType: "",
    countries: [],
    dateRange: {
      startDate: null,
      endDate: null,
    },
    lastActivityDateRange: {
      startDate: null,
      endDate: null,
    },
    summaryFilter: "",
    platformFilter: "",
    excludeAppleLogin: false,
    showOnlyDeletedUsers: false, // Default: show all users (not only deleted)
    showOnlyNonVerifiedUsers: false, // Default: show all users (not only non-verified)
    professionFilter: "all", // Default: show all professions
    sortBy: "created_at", // Default: sort by joined date
    sortDirection: "desc", // Default: newest first
    professions: [] as any[],
  },
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
};

export const fetchUsers = createAsyncThunk(
  "user/fetchUsers",
  async (
    params: {
      filters?: Partial<UserFilters>;
      page?: number;
      limit?: number;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const { filters = {}, page = 0, limit = 10 } = params;
      const result = await UserService.getUsersOptimized(filters, page, limit);

      return result;
    } catch (error) {
      return rejectWithValue((error as any).message || "Failed to fetch users");
    }
  }
);

export const fetchUsersWithPagination = createAsyncThunk(
  "user/fetchUsersWithPagination",
  async (
    params: {
      filters?: Partial<UserFilters>;
      page: number;
      limit: number;
      loading?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { filters = {}, page, limit, loading = true } = params;
      const result = await UserService.getUsersOptimized(filters, page, limit);

      return result;
    } catch (error) {
      return rejectWithValue((error as any).message || "Failed to fetch users");
    }
  }
);

export const fetchUserById = createAsyncThunk(
  "user/fetchUserById",
  async (userId: string, { rejectWithValue }) => {
    try {
      const user = await ApiService.get<any>(`/v1/users/${userId}/details`);

      // Some APIs return 200 with { status: 0, code: 400, message: "..." }.
      // Treat those payloads as errors so UI can handle them properly.
      if (
        !user ||
        user?.status === 0 ||
        Number(user?.code || 0) >= 400 ||
        !user?.data
      ) {
        const message = user?.message || "User not found";
        const code = user?.code ? ` (code:${user.code})` : "";
        return rejectWithValue(`${message}${code}`);
      }

      if (!user) {
        throw new Error("User not found");
      }

      return user;
    } catch (error) {
      const err = error as any;
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to fetch user";
      const apiCode = err?.response?.data?.code || err?.response?.status;
      return rejectWithValue(apiCode ? `${apiMessage} (code:${apiCode})` : apiMessage);
    }
  }
);

export const createUser = createAsyncThunk(
  "user/createUser",
  async (userData: Omit<AppUser, "id" | "joinedDate">, { rejectWithValue }) => {
    try {
      const newUser = await UserService.createUser(userData);
      return newUser;
    } catch (error) {
      return rejectWithValue((error as any).message || "Failed to create user");
    }
  }
);

export const cancelFreeTrial = createAsyncThunk(
  "user/cancelFreeTrial",
  async (userId: string, { rejectWithValue }) => {
    try {
      const updatedUser = await ApiService.put<AppUser>(
        `users/${userId}/cancel-trial`
      );
      return updatedUser;
    } catch (error) {
      return rejectWithValue(
        (error as any).message || "Failed to cancel free trial"
      );
    }
  }
);
export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (
    { id, userData }: { id: string; userData: Partial<AppUser> },
    { rejectWithValue }
  ) => {
    try {
      const updatedUser = await ApiService.put<AppUser>(
        `/v1/users/${id}/status?status=${userData.status_id}`
      );
      if (!updatedUser) {
        throw new Error("User not found");
      }
      return updatedUser;
    } catch (error) {
      return rejectWithValue((error as any).message || "Failed to update user");
    }
  }
);

export const updateUserSuspension = createAsyncThunk(
  "user/updateUserSuspension",
  async (
    { id, suspensionData }: { id: string; suspensionData: any },
    { rejectWithValue }
  ) => {
    try {
      const updatedUser = await ApiService.put<AppUser>(
        `/v1/users/${id}/suspend`,
        suspensionData
      );
      if (!updatedUser) {
        throw new Error("User not found");
      }
      return updatedUser;
    } catch (error) {
      return rejectWithValue((error as any).message || "Failed to update user");
    }
  }
);

export const ReactivateUserSuspension = createAsyncThunk(
  "user/reactivate",
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const updatedUser = await ApiService.put<AppUser>(
        `/v1/users/${id}/reactivate`
      );
      if (!updatedUser) {
        throw new Error("User not found");
      }
      return updatedUser;
    } catch (error) {
      return rejectWithValue((error as any).message || "Failed to update user");
    }
  }
);

export const updateUserByTestStatus = createAsyncThunk(
  "user/updateUserByTestStatus",
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const updatedUser = await ApiService.put<AppUser>(
        `users/${id}/test-user`
      );
      if (!updatedUser) {
        throw new Error("User not found");
      }
      return updatedUser;
    } catch (error) {
      return rejectWithValue((error as any).message || "Failed to update user");
    }
  }
);

export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async (userId: string, { rejectWithValue }) => {
    try {
      await ApiService.delete(`v1/users/${userId}`);
      return userId;
    } catch (error) {
      return rejectWithValue((error as any).message || "Failed to delete user");
    }
  }
);

export const assignFreeTrial = createAsyncThunk(
  "user/assignFreeTrial",
  async (
    { userId, plan }: { userId: string; plan: any },
    { rejectWithValue }
  ) => {
    try {
      const updatedUser = await ApiService.put<AppUser>(
        `users/${userId}/assign-trial`,

        {
          trial_plan_id: plan.trial_plan_id,
          duration_value: plan.duration_value,
          duration_unit: plan.duration_unit,
        }
      );
      return updatedUser;
    } catch (error) {
      return rejectWithValue(
        (error as any).message || "Failed to assign free trial"
      );
    }
  }
);

export const fetchLLMModelsByPlan = createAsyncThunk(
  "user/fetchLLMModelsByPlan",
  async (_, { rejectWithValue }) => {
    try {
      const llmModels = await ApiService.get<any>(`/get-llm-models-by-plan`);
      return llmModels?.data || [];
    } catch (error) {
      return rejectWithValue(
        (error as any).message || "Failed to fetch LLM models"
      );
    }
  }
);

export const fetchProfessions = createAsyncThunk(
  "user/fetchProfessions",
  async (_, { rejectWithValue }) => {
    try {
      const professions = await ApiService.post<any>(`/get-professions-list`);
      return professions?.data || [];
    } catch (error) {
      return rejectWithValue(
        (error as any).message || "Failed to fetch professions"
      );
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<UserFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (
      state,
      action: PayloadAction<Partial<UserState["pagination"]>>
    ) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearUsers: (state) => {
      state.users = [];
      state.pagination.total = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;

        state.users = action.payload.users;
        state.pagination.total = action.payload.total;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUsersWithPagination.pending, (state, action) => {
        const { loading } = action.meta.arg as { loading?: boolean };
        state.isPaginationLoading = !loading ? false : true;
        state.error = null;
      })
      .addCase(fetchUsersWithPagination.fulfilled, (state, action) => {
        state.isPaginationLoading = false;
        state.users = action.payload.users;
        const totalStats = (action.payload as any).statistics;
        state.overallStats = {
          total_revenue: totalStats?.total_revenue || 0,
          total_active_users: totalStats?.total_active_users || 0,
          total_users: totalStats?.total_users || 0,
          active_users: totalStats?.active_users || 0,
          subscribed_users: totalStats?.subscribed_users || 0,
          test_users: totalStats?.test_users || 0,
          real_users: totalStats?.real_users || 0,
        };
        state.pagination.page = action.payload.page;
        state.pagination.pageSize = action.payload.limit;
        state.pagination.total = action.payload.total;
      })
      .addCase(fetchUsersWithPagination.rejected, (state, action) => {
        state.isPaginationLoading = false;
        state.error = action.payload as string;
      })

      // Fetch User By ID
      .addCase(fetchUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userDetails = action.payload.data;
        // Update the user in the list if it exists
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create User
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users.push(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update User
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex(
          (user: any) => user.id === action.payload.id
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = state.users.filter(
          (user: any) => user.uuid !== action.payload
        );
        state.pagination.total -= 1;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Assign Free Trial
      .addCase(assignFreeTrial.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignFreeTrial.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex(
          (user: any) => user.id === action.payload.id
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(assignFreeTrial.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch LLM Models By Plan
      .addCase(fetchLLMModelsByPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLLMModelsByPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.llmModelsPlans = action.payload;
      })
      .addCase(fetchLLMModelsByPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Professions
      .addCase(fetchProfessions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProfessions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filters.professions = action.payload.professions.map(
          (profession: any) => ({
            value: profession.id,
            label: profession.name,
          })
        );
      })
      .addCase(fetchProfessions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setFilters,
  setPagination,
  clearUsers,
} = userSlice.actions;
export default userSlice.reducer;
